import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTenant } from "../contexts/TenantContext";
import { restoreUser, startImpersonationSession, endImpersonationSession } from "../store/authSlice";
import { getGlobalDashboard, startImpersonation, endImpersonationAudit } from "../api/globalAdminApi";
import apiClient, { setActiveTenant, setImpersonationToken, clearImpersonationToken } from "../api/apiClient";
import { setCurrentUser } from "../utils/auth";
import { roleLabelFor, rememberRecentCenter } from "./globalAdmin/tenantSwitcher/tenantModel";
import TenantList from "./globalAdmin/tenantSwitcher/TenantList";
import TenantWorkspace from "./globalAdmin/tenantSwitcher/TenantWorkspace";
import AuditLogView from "./globalAdmin/audit/AuditLogView";
import SystemHealthView from "./globalAdmin/health/SystemHealthView";
import TimeTravelView from "./globalAdmin/clock/TimeTravelView";
import { GLOBAL_ADMIN_PATHS } from "./globalAdmin/paths";


function decodeJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/** Shown only while impersonating a centre that is not in the loaded list, so the workspace still has a header. */
function fallbackTenant(impersonating) {
  return {
    id: impersonating?.tenantId, name: impersonating?.tenantName || "Training center", code: "", region: "",
    program: "", support: "", courses: 0, students: 0, instructors: 0,
  };
}

/** Global-admin screens share the .ga-root style scope and refuse everyone else. */
function GlobalAdminOnly({ testId, children }) {
  const { user } = useTenant();
  if (!user?.roles?.includes("GLOBAL_ADMIN")) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>You don't have access to this page.</Typography>
      </Box>
    );
  }
  return (
    <Box className="ga-root ga-page" data-testid={testId}>
      <div className="ga-page-main">{children}</div>
    </Box>
  );
}

function useGlobalTenants() {
  const { user } = useTenant();
  const isGlobalAdmin = !!user?.roles?.includes("GLOBAL_ADMIN");
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    let cancelled = false;
    getGlobalDashboard()
      .then((list) => {
        if (cancelled) return;
        setTenants(Array.isArray(list) ? list : []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setTenants([]);
        setError(err?.message || "Failed to load training centers.");
      });
    return () => { cancelled = true; };
  }, [isGlobalAdmin]);

  return { tenants, error };
}

export function GlobalAdminTenantsPage() {
  const { activeTenantId, switchTenant } = useTenant();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tenants, error } = useGlobalTenants();
  const [tenantQuery, setTenantQuery] = useState("");
  const impersonating = useSelector((state) => state.auth.impersonating);
  const [impersonationError, setImpersonationError] = useState(null);
  const impersonateSessionRef = useRef(null);
  const endingSessionRef = useRef(false);

  const selectedTenant = useMemo(() => tenants.find((t) => String(t.id) === String(activeTenantId)) || null, [tenants, activeTenantId]);

  const handleImpersonate = useCallback(async (imp) => {
    setImpersonationError(null);
    let result;
    try {
      result = await startImpersonation(imp.tenantId, imp.id, imp.role);
      setImpersonationToken(result.token);
      setActiveTenant(String(result.tenantId));
    } catch {
      setImpersonationError("Unable to start impersonation. Check your admin session and try again.");
      return;
    }

    const payload = decodeJwtPayload(result.token);
    if (payload) {
      const rawTenantRoles = JSON.parse(payload.tenantRoles || "{}");
      const tenantRoles = {};
      for (const [tid, rolesStr] of Object.entries(rawTenantRoles)) {
        tenantRoles[tid] = rolesStr.split(",").map((r) => r.trim()).filter(Boolean);
      }
      setCurrentUser({
        id: payload.userId,
        username: payload.sub,
        email: payload.email,
        fullName: imp.name,
        roles: tenantRoles[String(result.tenantId)] || [],
        tenantId: String(result.tenantId),
        tenantRoles,
      });
      dispatch(restoreUser({
        userId: payload.userId,
        email: payload.email,
        username: payload.sub,
        fullName: imp.name,
        roles: [],
        tenantRoles,
      }));
    }

    impersonateSessionRef.current = {
      sessionId: result.sessionId || null,
      tenantId: imp.tenantId,
      targetName: imp.name,
      startTime: Date.now(),
    };
    dispatch(startImpersonationSession({
      name: imp.name,
      firstName: imp.name.split(" ")[0],
      role: imp.role,
      roleLabel: roleLabelFor(imp.role),
      initials: imp.initials,
      tenantName: imp.tenantName,
      tenantId: imp.tenantId,
      sessionId: result.sessionId || null,
      startTime: Date.now(),
    }));
    navigate("/");
  }, [dispatch, navigate]);

  const handleExit = useCallback(async () => {
    if (endingSessionRef.current) return false;
    const sess = impersonateSessionRef.current || impersonating;
    setImpersonationError(null);
    if (!sess?.sessionId) {
      setImpersonationError("Unable to end impersonation: the session identifier is missing. Sign out and sign in again.");
      return false;
    }
    endingSessionRef.current = true;
    try {
      const durationSec = Math.round((Date.now() - (sess.startTime || Date.now())) / 1000);
      await endImpersonationAudit({ sessionId: sess.sessionId, tenantId: sess.tenantId, targetName: sess.targetName || sess.name, durationSec });
      impersonateSessionRef.current = null;
    } catch {
      setImpersonationError("Unable to end impersonation. Your session is still active; try again.");
      return false;
    } finally {
      endingSessionRef.current = false;
    }
    clearImpersonationToken();
    setActiveTenant(null);
    dispatch(endImpersonationSession());
    try {
      const res = await apiClient.get("/api/v1/auth/me");
      const data = res.data;
      setCurrentUser({
        ...data,
        tenantId: Object.keys(data.tenantRoles || {})[0] || null,
      });
      dispatch(restoreUser({
        userId: data.id,
        email: data.email,
        username: data.username,
        fullName: data.fullName || null,
        roles: data.roles || [],
        tenantRoles: data.tenantRoles || {},
        agencyId: data.agencyId || null,
        agencyName: data.agencyName || null,
      }));
    } catch {
      setImpersonationError("Impersonation ended, but your admin session could not be restored. Sign in again.");
      return false;
    }
    navigate(GLOBAL_ADMIN_PATHS.tenants);
    return true;
  }, [dispatch, navigate, impersonating]);

  const openTenant = useCallback(async (tenant) => {
    if (impersonating && !(await handleExit())) return;
    rememberRecentCenter(tenant.id);
    switchTenant(tenant.id);
  }, [switchTenant, impersonating, handleExit]);

  return (
    <GlobalAdminOnly testId="global-admin-tenants-page">
      {impersonationError && <Alert severity="error" sx={{ mb: 2 }}>{impersonationError}</Alert>}
      {!selectedTenant && !impersonating ? (
        <TenantList
          tenants={tenants}
          error={error}
          query={tenantQuery}
          onQuery={setTenantQuery}
          onOpen={openTenant}
        />
      ) : (
        <TenantWorkspace
          tenant={selectedTenant || fallbackTenant(impersonating)}
          impersonating={impersonating}
          onBack={async () => {
            if (impersonating && !(await handleExit())) return;
            switchTenant(null);
          }}
          onImpersonate={handleImpersonate}
          onExit={handleExit}
        />
      )}
    </GlobalAdminOnly>
  );
}

export function GlobalAdminAuditLogPage() {
  const { user } = useTenant();
  const { tenants } = useGlobalTenants();
  return (
    <GlobalAdminOnly testId="global-admin-audit-log-page">
      <AuditLogView tenants={tenants} currentUserName={user?.name} />
    </GlobalAdminOnly>
  );
}

export function GlobalAdminSystemHealthPage() {
  return (
    <GlobalAdminOnly testId="global-admin-system-health-page">
      <SystemHealthView />
    </GlobalAdminOnly>
  );
}

export function GlobalAdminTimeTravelPage() {
  return (
    <GlobalAdminOnly testId="global-admin-time-travel-page">
      <TimeTravelView env="local" />
    </GlobalAdminOnly>
  );
}
